import { useForm } from "../../hooks/useForm";
import Section from "../layout/Section";
import FormField from "./FormField";
import type { ChangeEvent } from "react";
import { useAuthStore } from "@/stores/authStore";

// TODO: Some fields (customer name, phone) could be pre-populated from the survey record

export default function BasicInfoForm() {
  const { name, email, address, setField } = useForm();
  const { phone, phoneValid, phoneWarning, setPhone } = useForm();
  const user = useAuthStore((s) => s.user);
  const sectionTitle =
    user?.role === "builder"
      ? "Basic Customer's Information"
      : "Basic Information";

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "");
    const limited = digits.slice(0, 10);

    let formatted = "";
    if (limited.length > 0) {
      formatted = "(";
      formatted += limited.slice(0, 3);
      if (limited.length > 3) {
        formatted += ") ";
        formatted += limited.slice(3, 6);
        if (limited.length > 6) {
          formatted += "-";
          formatted += limited.slice(6);
        }
      }
    }

    const isValid = limited.length === 10;
    const warning =
      limited.length > 0 && limited.length < 10
        ? `Phone number must have 10 digits (currently ${limited.length})`
        : "";

    setPhone(formatted || limited, isValid, warning);
  };

  return (
    <Section title={sectionTitle} className="py-4 px-1">
      <p className="text-md mt-2 mb-2">
        Please provide the following information:
      </p>

      <div className="w-full mt-4 mb-4 flex flex-col gap-4 md:gap-0 md:flex-row md:flex-wrap md:justify-evenly">
        <FormField
          label="Name"
          tooltip="Enter your full name, last name, first name, exactly as you want it to appear on the official document."
          className="w-full md:w-auto"
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setField("name", e.target.value)}
            placeholder="John Scott"
            className="border border-gray-300 rounded px-2 py-1 ml-1 w-full md:w-auto"
          />
        </FormField>

        <FormField
          label="Mail"
          tooltip="Enter a valid email for contact and to send you the PDF with your configuration and any necessary updates that may also be required by your HOA or County City."
          className="w-full md:w-auto"
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setField("email", e.target.value)}
            placeholder="johnscott@mail.com"
            className="border border-gray-300 rounded px-2 py-1 ml-1 w-full md:w-auto"
          />
        </FormField>

        <FormField
          label="Phone number"
          tooltip="Add your number in case they need to contact you to confirm lot details or installation."
          className="w-full md:w-auto"
        >
          <div className="block md:inline-block">
            <input
              type="tel"
              value={phone}
              onChange={handleChange}
              placeholder="(123) 456-7890"
              className={`border rounded pl-2 pr-12 md:px-2 py-1 ml-1 md:ml-1 w-full md:w-auto ${
                !phoneValid && phone ? "border-red-500" : "border-gray-300"
              }`}
            />
          </div>
        </FormField>
        {phoneWarning && (
          <div className="text-red-500 text-sm mt-1">{phoneWarning}</div>
        )}
      </div>
      <div className="w-full md:w-auto">
        <FormField
          label="Address"
          tooltip="Write the address where the shed will be installed. This data is key to identify your property in the system and identify your lot."
          className="w-full md:w-auto"
        >
          <input
            type="text"
            value={address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="123 Main St, City, State"
            className="border border-gray-300 rounded pr-0 pl-2 py-1 ml-1 w-full md:w-80"
          />
        </FormField>
      </div>
    </Section>
  );
}
